import { Lottery } from "@sazka/lotteries/js/models/lottery";
import { LotteryCheckout } from "@/lotteries/online/shared/lottery-checkout/lottery-checkout";
import { DurationState } from "@/lotteries/online/shared/duration-state";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import useBetSuccess from "../../composables/bet-success/use-bet-success";
import { useWagerBoardStore } from "../stores/use-wager-board-store";
import { usePpWagerDurationStore } from "../stores/use-wager-duration-store";
import { Wager } from "../model/wager";
import { usePpWagerPriceCalculator } from "./use-wager-price-calculator";
import { usePpWagerConfiguration } from "./use-wager-configuration";
import { DateTimeUtils } from "@sazka/utils/date-time-utils";
import { ProductPageAnalytics } from "../analytics/analytics-product-page";
import usePpBetAnalytics from "../composables/use-pp-bet-analytics";
import { SubscriptionEndType } from "../model/subscription-end-type";

const { toggleShowSuccess } = useBetSuccess();

let checkout: LotteryCheckout = null;
let lottery: Lottery = null;

// TODO - review whole checkout flow for PP, fix Wager type
export const usePPWagerCheckout = () => {
    const durationStore = usePpWagerDurationStore();

    const boardStore = useWagerBoardStore();

    const startCheckoutFlow = async (isRestarted = false): Promise<Record<string, any>> => {
        if (isRestarted) {
            // Need to do this in order to get wager which is retored from the session, after returning from gateway
            updateCheckout();
        }

        const data = await checkout.start({
            isRestarted,
            isSubscriptionInternal: () => durationStore.durationType === DurationState.Subscription,
        });

        logPurchaseAnalytics(data?.wagerID ?? 0);

        hideElements();
        boardStore.clearWager();
        boardStore.isBetSuccessfull = true;
        durationStore.clearDuration();

        const price = data?.wager?.price ?? data?.wagers?.reduce((acc, wager) => acc + wager.price, 0);

        // TODO: need to adjust this to account for multibet (third argument)
        toggleShowSuccess({
            price: price,
            wagerId: data?.wagerID,
            wagerIds: data?.wagerIds,
            isMultibetPageBet: false,
            isSubscriptionBet: durationStore.durationType === DurationState.Subscription,
            subscriptionIdBet: data?.subscriptionId,
        });

        return data;
    };

    function hideElements() {
        const navigation = document.getElementById("pp-navigation");
        const bet = document.getElementById("pp-bet");
        const carousel = document.getElementById("pp-quick-wager-carousel");

        if (navigation) navigation.style.display = "none";
        if (bet) bet.style.display = "none";
        if (carousel) carousel.style.display = "none";
    }

    function logPurchaseAnalytics(wagerId: number) {
        const { bettingMode, bettingModePosition, initialBoardsCount, columnsFillState, boardsFillTracking } =
            usePpBetAnalytics();
        const { totalPrice } = usePpWagerPriceCalculator();
        const { config } = usePpWagerConfiguration();

        const addonGameName = Analytics.getAddonName(config.lottery, boardStore.isAddonPlayed);
        const gameCode = LotteryParser.analyticsCodeName(config.lottery);
        let bettingSystem = null;

        const columnsFillTypeString =
            [...new Set(boardsFillTracking.value)].filter((value) => value !== "").join(",") || "";

        let tier = null;
        if (lottery === Lottery.extraRenta) {
            tier = boardStore.wagerStakes[0] === 25 ? "low" : "high";
        }

        if (durationStore.durationType == DurationState.Subscription) {
            bettingSystem = "standingOrder";

            ProductPageAnalytics.pushMoneyBetStanding({
                transactionId: wagerId.toString(),
                gameCode: gameCode,
                value: totalPrice.value,
                bettingSystem: bettingSystem,
                repetitionEndDate: durationStore.subscriptionEnd
                    ? DateTimeUtils.formatDate(new Date(durationStore.subscriptionEnd)).replace(/\s/g, "")
                    : "infinity",
                columns: `${initialBoardsCount.value}/${boardStore.boardCount}`,
                columnsFillType: columnsFillTypeString,
                columnsFillState: columnsFillState.value,
                addonGame: addonGameName,
                bettingMode: bettingMode.value,
                position: bettingModePosition.value,
                tier: tier,
            });
        } else {
            bettingSystem = durationStore.durationType == DurationState.Multi ? "multiDraw" : "singleDraw";
            const columns =
                lottery === Lottery.rychla6
                    ? `${3}/${boardStore.getBoardsForCheckout.length}`
                    : `${initialBoardsCount.value}/${boardStore.boardCount}`;

            ProductPageAnalytics.pushMoneyBetSingleOrMultiDraw({
                transactionId: wagerId.toString(),
                gameCode: gameCode,
                value: totalPrice.value,
                bettingSystem: bettingSystem,
                columns: columns,
                columnsFillType: columnsFillTypeString,
                columnsFillState: columnsFillState.value,
                addonGame: addonGameName,
                bettingMode: bettingMode.value,
                position: bettingModePosition.value,
                tier: tier,
            });
        }

        ProductPageAnalytics.pushPurchase({
            orderId: wagerId,
            gameCode: gameCode,
            value: totalPrice.value,
            bettingSystem: bettingSystem,
            addonGame: addonGameName,
            boards: boardStore.boardCount,
            draws: durationStore.getDurationLength,
            bettingMode: bettingMode.value,
            drawNames: durationStore.drawNames,
        });
    }

    const storesToWager = (): Wager => {
        lottery = usePpWagerConfiguration().config.lottery;

        const wager = {
            boards: boardStore.isSystem ? [boardStore.systemBoard] : boardStore.getBoardsForCheckout,
            isAddonPlayed: boardStore.isAddonPlayed,
            addonNumbers: boardStore.isAddonPlayed ? boardStore.addonNumbers : [],
            stakes: lottery === Lottery.kasicka && !boardStore.isSystem ? [] : boardStore.wagerStakes,
            multiplier: boardStore.multiplier,
            drawNames: durationStore.drawNames,
            duration: durationStore.getDurationLength,
            durationChoice: durationStore.durationType,
            subscriptionEnd:
                durationStore.subscriptionEndType === SubscriptionEndType.Date
                    ? DateTimeUtils.getLocalIsoString(new Date(durationStore.subscriptionEnd))
                    : null,
            subscriptionName: durationStore.subscriptionName,
            sendNotification: durationStore.notification,
        } satisfies Wager;

        return wager;
    };

    function updateCheckout(): void {
        checkout = new LotteryCheckout({
            betServiceUrl: `wager/${lottery}`,
            httpMethod: "POST",
            key: `wager-online-${lottery}`,
            getCost: () => usePpWagerPriceCalculator().totalPrice.value,
            useDefaultAnalytics: false,
            getData: () => storesToWager(),
            isSubscriptionInternal: () => usePpWagerDurationStore().durationType === DurationState.Subscription,
            restartFlow: () => {
                startCheckoutFlow(true);
            },
        });
    }

    const updateAndStartCheckout = async (): Promise<Record<string, any>> => {
        updateCheckout();
        const data = await startCheckoutFlow();
        return data;
    };

    const init = (checkoutLottery?: Lottery): void => {
        if (checkout) return;
        if (!checkoutLottery) return;

        lottery = checkoutLottery;
        updateCheckout();
    };

    return {
        init,
        updateAndStartCheckout,
    };
};
